import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import type { InvokeResponseBase } from '../../lib/types';

interface Decret {
  jort_number: string;
  publication_date: string;
  title: string;
  domain: 'fiscal' | 'social' | 'commercial' | 'autre';
  summary: string;
  effective_date?: string;
  impacted_concepts: string[];
  relevance_score: number;
}

interface VeilleurResult {
  period: { from: string; to: string };
  decrets: Decret[];
  decrets_count: number;
  high_impact_count: number;
}

const DOMAIN_STYLE: Record<Decret['domain'], string> = {
  fiscal: 'bg-rose-100 text-rose-900 border-rose-300',
  social: 'bg-blue-100 text-blue-900 border-blue-300',
  commercial: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  autre: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

export function VeilleurJortPage() {
  const [from, setFrom] = useState('2026-03-01');
  const [to, setTo] = useState('2026-05-31');
  const [domain, setDomain] = useState<'' | Decret['domain']>('');
  const [minRelevance, setMinRelevance] = useState(0.5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        from_date: from,
        to_date: to,
        min_relevance: minRelevance,
        ...(domain ? { domain } : {}),
      };
      const res = await api.invokeAgent('veilleur_jort', { payload, fiscal_reference_date: to });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Veilleur JORT"
      module="Fiscal · Phase 3"
      model="Opus 4.7"
      description={
        <>
          Veille du Journal Officiel de la République Tunisienne — décrets, arrêtés, lois de finances.
          Mode fixture en bootstrap (5 décrets seed représentatifs) ; scraping live via JORT.tn en v0.2.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Filtres">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Période — début"><input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} required /></Field>
            <Field label="Période — fin"><input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} required /></Field>
            <Field label="Domaine">
              <select className={inputCls} value={domain} onChange={(e) => setDomain(e.target.value as typeof domain)}>
                <option value="">Tous</option>
                <option value="fiscal">Fiscal</option>
                <option value="social">Social</option>
                <option value="commercial">Commercial</option>
                <option value="autre">Autre</option>
              </select>
            </Field>
            <Field label={`Pertinence min : ${minRelevance.toFixed(2)}`} hint="Seuil de score d'impact PME">
              <input type="range" min={0} max={1} step={0.05} className="w-full" value={minRelevance} onChange={(e) => setMinRelevance(parseFloat(e.target.value))} />
            </Field>
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Veille en cours…' : 'Lancer la veille'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <VeilleurResultView result={result as VeilleurResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function VeilleurResultView({ result }: { result: VeilleurResult }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="text-xs text-taupe">Période <strong className="font-mono">{result.period.from} → {result.period.to}</strong></span>
        <span className="px-2 py-1 rounded bg-cream/60 border border-border text-xs">{result.decrets_count} décret{result.decrets_count > 1 ? 's' : ''}</span>
        {result.high_impact_count > 0 && (
          <span className="px-2 py-1 rounded bg-red-100 text-red-900 border border-red-300 text-xs">
            {result.high_impact_count} impact élevé
          </span>
        )}
      </div>
      {result.decrets.length === 0 ? (
        <div className="card text-sm text-taupe italic">Aucun décret pertinent sur cette période avec ces critères.</div>
      ) : (
        <div className="space-y-3">
          {result.decrets.map((d) => (
            <div key={d.jort_number} className="card !p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded border uppercase tracking-wider ${DOMAIN_STYLE[d.domain]}`}>{d.domain}</span>
                  <span className="font-mono text-xs text-taupe">{d.jort_number}</span>
                  <span className="text-xs text-taupe">publié le <strong className="font-mono">{d.publication_date}</strong></span>
                  {d.effective_date && (
                    <span className="text-xs text-taupe">effet au <strong className="font-mono">{d.effective_date}</strong></span>
                  )}
                </div>
                <ScoreBar score={d.relevance_score} />
              </div>
              <h4 className="font-serif font-bold mb-1">{d.title}</h4>
              <p className="text-sm text-ink leading-relaxed">{d.summary}</p>
              {d.impacted_concepts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {d.impacted_concepts.map((c) => (
                    <span key={c} className="text-[11px] px-1.5 py-0.5 rounded bg-cream/70 border border-border">{c}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = score >= 0.85 ? 'bg-red-500' : score >= 0.7 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-zinc-200 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-taupe">{pct}%</span>
    </div>
  );
}
